import React, { useEffect, useState } from "react";
import './Post.css';
import axios from "axios";
import { Heart, MessageCircle, Share2, MoreHorizontal, Send } from "lucide-react";

export default function Post({ post }) {
  const user = JSON.parse(localStorage.getItem("user"));
  const userId = user?._id;
  const [liked, setLiked] = useState(false);
  const [likes, setLikes] = useState(post.likes);
  const [comments, setComments] = useState(post.comments || []);
  const [commentText, setCommentText] = useState("");
  const [showComments, setShowComments] = useState(false);
  const [isCommenting, setIsCommenting] = useState(false);
  const token = localStorage.getItem("token");

  useEffect(() => {
    if (!userId) return;
    setLiked(post.likedBy?.includes(userId));
    setLikes(post.likes);
    setComments(post.comments || []);
  }, [post, userId]);

  const handleLike = async () => {
    const res = await axios.post(`${process.env.REACT_APP_API_URL}/posts/${post._id}/like`, {}, {
      headers: {Authorization: `Bearer ${token}`}
    });
    setLiked(res.data.liked);
    setLikes(res.data.likes);
  }

  const handleComment = async () => {
    if(!commentText.trim()) return;
    setIsCommenting(true);
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/post/${post._id}/comment`,
        {text: commentText},
        {headers: {Authorization: `Bearer ${token}`}}
      );
      setComments(res.data);
      setCommentText("");
    } finally {
      setIsCommenting(false);
    }
  }

  const formatTime = (dateString) => {
    const now = new Date();
    const postDate = new Date(dateString);
    const seconds = Math.floor((now - postDate) / 1000);

    if (seconds < 60) return "Just now";
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d`;
    const weeks = Math.floor(days / 7);
    if (weeks < 4) return `${weeks}w`;
    const months = Math.floor(days / 30);
    if (months < 12) return `${months}mo`;
    const years = Math.floor(days / 365);
    return `${years}y`;
  };

  return (
    <div className="post">
      <div className="post-header">
        <div className="post-header-left">
          <img 
            className="post-avatar" 
            src={
              post.author?.profilepic
                ? `${process.env.REACT_APP_FILE_URL}${post.author.profilepic}`
                : "https://api.dicebear.com/7.x/avataaars/svg?seed=" + (post.author?._id || "default")
            }
            alt={post.author?.name} 
          />
          <div className="post-meta">
            <h4 className="post-author">{post.author?.name}</h4>
            <div className="post-timestamp">
              <span>{formatTime(post.createdAt)}</span>
              <span className="dot-separator">•</span>
              <span className="visibility-badge">Public</span>
            </div>
          </div>
        </div>
        <button className="post-options">
          <MoreHorizontal size={20} />
        </button>
      </div>

      <div className="post-body">
        <p className="post-text">{post.text}</p>
        {post.image && (
          <div className="post-media">
            <img className="post-media-image" src={post.image} alt="post" />
          </div>
        )}
      </div>

      <div className="post-engagement">
        <div className="reaction-summary">
          <div className="comment-summary">
            {comments.length > 0 && (
              <span className="comment-count">{comments.length} comments</span>
            )}
          </div>
        </div>
      </div>
      
      <div className="post-actions">
        <button
          className={`action-item ${liked ? 'active' : ''}`}
          onClick={handleLike}
        >
          <div className="action-icon-wrapper">
            <Heart 
              size={20} 
              fill={liked ? "#ef4444" : "none"}
              stroke={liked ? "#ef4444" : "currentColor"}
            />
            {liked && <span className="action-active-dot"></span>}
          </div>
          <span className="action-text">Like</span>
          {liked && <span className="action-badge">Liked</span>}
          {likes > 0 && <span className="action-count">{likes}</span>}
        </button>

        <button 
          className="action-item"
          onClick={() => setShowComments(!showComments)}
        >
          <div className="action-icon-wrapper">
            <MessageCircle size={20} />
          </div>
          <span className="action-text">Comment</span>
          {comments.length > 0 && (
            <span className="action-count">{comments.length}</span>
          )}
        </button>

        <button className="action-item">
          <div className="action-icon-wrapper">
            <Share2 size={20} />
          </div>
          <span className="action-text">Share</span>
        </button>
      </div>

      {showComments && (
        <div className="comments-container">
          <div className="comment-form">
            <img src={
                  user?.profilepic
                    ? `${process.env.REACT_APP_FILE_URL}${user.profilepic}`
                    : user?.avatar 
                      ? `${process.env.REACT_APP_FILE_URL}${user.avatar}`
                      : "https://api.dicebear.com/7.x/avataaars/svg?seed=" + (user?.userid || 'default')
                } alt="profilepic" className="comment-avatar" />
            <div className="comment-input-area">
              <input
                type="text"
                placeholder="Add a comment..."
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleComment()}
                className="comment-input"
              />
              <button 
                onClick={handleComment} 
                disabled={!commentText.trim() || isCommenting}
                className="comment-submit"
              >
                {isCommenting ? 'Posting...' : <Send size={16} />}
              </button>
            </div>
          </div>
          
          <div className="comments-list">
            {comments.length > 0 ? (
              comments.map((c, index) => (
                <div key={index} className="comment-item">
                  <img
                    src={
                      c.user?.profilepic
                        ? `${process.env.REACT_APP_FILE_URL}${c.user.profilepic}`
                        : "https://api.dicebear.com/7.x/avataaars/svg?seed=" + (c.user?._id || "default")
                    }
                    alt={c.user?.name}
                    className="comment-avatar"
                  />
                  <div className="comment-content">
                    <div className="comment-bubble">
                      <span className="comment-author">{c.user?.name}</span>
                      <p className="comment-text">{c.text}</p>
                    </div>
                    <div className="comment-metadata">
                      <button className="comment-action">Like</button>
                      <span className="separator">·</span>
                      <button className="comment-action">Reply</button>
                      <span className="separator">·</span>
                      <span className="comment-time">Just now</span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-comments">
                <p>No comments yet. Start the conversation.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}